import Loading from '@app/statics/Loading';
import NotFound from '@app/statics/NotFound';
import { ButtonCore } from '@components';
import {
  getMobileStoryDetailsTransformer,
  getStoryDetailsTransformer,
} from '@coreStory/transformer';
import {
  useGetStoryDetails,
  useUpdateStoryVisibility,
  useGetStoryAnalysis,
  useGetStoryVideoDownload,
  useGetStoryRequestInfo,
  useGetRecommendedStories,
  useStoryInSights,
} from '@coreStory/useCase';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './index.module.scss';
import MobileView from './mobileView';
import WebView from './webView';
import {
  StoryButtons,
  VisibilityModal,
  RateModal,
  LearningInsightModal,
  ActivityModal,
  HashtagModal,
  StoryRequestModal,
  RecommendedStoriesModal,
} from './components';
import StoryInsight from './components/StoryInsight/StoryInsight';

const ViewStory = () => {
  const width = window.innerWidth;
  const { id } = useParams();
  const { mutate, data, isLoading } = useGetStoryDetails();
  const navigate = useNavigate();
  const { mutate: updateVisibility, isLoading: isUpdatingVisibility } =
    useUpdateStoryVisibility();
  const {
    mutate: getAnalysis,
    data: analysisData,
    isLoading: isAnalysisLoading,
    error: analysisError,
  } = useGetStoryAnalysis();
  const { mutate: getVideoDownload, isLoading: isVideoDownloadLoading } =
    useGetStoryVideoDownload();
  const {
    mutate: getStoryRequestInfo,
    data: storyRequestData,
    isLoading: isStoryRequestLoading,
    error: storyRequestError,
  } = useGetStoryRequestInfo();
  const {
    mutate: getRecommendedStories,
    data: recommendedStoriesData,
    isLoading: isRecommendedStoriesLoading,
    error: recommendedStoriesError,
  } = useGetRecommendedStories();

  const {
    mutate: storyInSightMutation,
    data: storyInsights,
    isLoading: isStoryInSightLoading,
    error: storyInSightError,
  } = useStoryInSights();

  // Modal state
  const [isVisibilityModalVisible, setIsVisibilityModalVisible] =
    useState(false);
  const [isRateModalVisible, setIsRateModalVisible] = useState(false);
  const [isLearningInsightModalVisible, setIsLearningInsightModalVisible] =
    useState(false);
  const [isActivityModalVisible, setIsActivityModalVisible] = useState(false);
  const [isHashtagModalVisible, setIsHashtagModalVisible] = useState(false);
  const [isStoryRequestModalVisible, setIsStoryRequestModalVisible] =
    useState(false);
  const [
    isRecommendedStoriesModalVisible,
    setIsRecommendedStoriesModalVisible,
  ] = useState(false);
  const [isStoryInSightVisible, setIsStoryInsightVisible] = useState(false);

  useEffect(() => {
    if (!id) navigate('/story', { replace: true });
    else mutate(Number(id));
  }, [id]);

  // Function to refresh story data
  const refreshStoryData = () => {
    if (id) {
      mutate(Number(id));
    }
  };

  // Button handlers
  const handleSetVisibility = () => {
    setIsVisibilityModalVisible(true);
  };

  const handleLearningInsight = () => {
    if (id) {
      getAnalysis(Number(id));
      setIsLearningInsightModalVisible(true);
    }
  };

  const handleActivity = () => {
    if (data?.length) {
      setIsActivityModalVisible(true);
    }
  };

  const handleHashtag = () => {
    if (data?.length) {
      setIsHashtagModalVisible(true);
    }
  };

  const handleStoryRequest = () => {
    if (id) {
      getStoryRequestInfo({ story_id: Number(id) });
      setIsStoryRequestModalVisible(true);
    }
  };

  const handleRate = () => {
    setIsRateModalVisible(true);
  };

  const handleRecommendedStories = () => {
    if (id) {
      getRecommendedStories({ story_id: Number(id) });
      setIsRecommendedStoriesModalVisible(true);
    }
  };
  // Modal handlers
  const handleConfirmVisibility = (status: boolean) => {
    if (id && data?.length) {
      updateVisibility({
        story_id: Number(id),
        is_private: status,
      });
      setIsVisibilityModalVisible(false);
    }
  };

  const handleCancelVisibility = () => {
    setIsVisibilityModalVisible(false);
  };

  const handleCancelRate = () => {
    setIsRateModalVisible(false);
  };

  const handleCancelLearningInsight = () => {
    setIsLearningInsightModalVisible(false);
  };

  const handleCancelActivity = () => {
    setIsActivityModalVisible(false);
  };

  const handleCancelHashtag = () => {
    setIsHashtagModalVisible(false);
  };

  const handleCancelStoryRequest = () => {
    setIsStoryRequestModalVisible(false);
  };

  const handleCancelRecommendedStories = () => {
    setIsRecommendedStoriesModalVisible(false);
  };

  const onDownload = () => {
    if (!id) return;

    const storyId = parseInt(id, 10);
    if (isNaN(storyId)) {
      toast.error('Invalid story ID');
      return;
    }

    getVideoDownload(
      { story_id: storyId },
      {
        onSuccess: (response) => {
          if (
            response.story_video_path &&
            response.story_video_path.trim() !== ''
          ) {
            // Video is ready, start download
            const link = document.createElement('a');
            link.href = response.story_video_path;
            link.download = `story-${storyId}.mp4`;
            link.target = '_blank';

            // Append to body, click, and remove
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toast.success('Download started!');
          } else {
            // Video is not ready, show status message
            toast.info(
              String(response.video_file_status || 'Video is not ready yet.'),
            );
          }
        },
        onError: (error) => {
          console.error('Video download check failed:', error);
          toast.error('Failed to check video status. Please try again.');
        },
      },
    );
  };

  const handleOpenStoryInsight = () => {
    if (id) {
      storyInSightMutation({ story_id: Number(id) });

      setIsStoryInsightVisible(true);
    }
  };

  return isLoading ? (
    <Loading text="Please wait ..." />
  ) : (
    <>
      {data?.length ? (
        <div className={styles['story_container']}>
          <div className={styles['title']}>
            <h1 className="title">{data[0]?.StoryName}</h1>
          </div>

          {width > 1000 ? (
            <WebView
              storyData={getStoryDetailsTransformer(data)}
              onStoryUpdated={refreshStoryData}
              onSetVisibility={handleSetVisibility}
            />
          ) : (
            <MobileView
              storyData={getMobileStoryDetailsTransformer(data)}
              onStoryUpdated={refreshStoryData}
              onSetVisibility={handleSetVisibility}
            />
          )}

          {/* Story Action Buttons */}

          <StoryButtons
            isMobile={width <= 1000}
            onLearningInsight={handleLearningInsight}
            onActivity={handleActivity}
            onHashtag={handleHashtag}
            onRate={handleRate}
            handleOpenStoryInsight={handleOpenStoryInsight}
            isVisibleStoryInsight={isStoryInSightVisible}
            onRecommendedStories={handleRecommendedStories}
            isOwnerStory={data[0].IsOwnerStory === 1}
            onDownload={onDownload}
            isDownloadLoading={isVideoDownloadLoading}
            onStoryRequest={handleStoryRequest}
            isStoryRequestLoading={isStoryRequestLoading}
            isRecommendedStoriesLoading={isRecommendedStoriesLoading}
          />

          {/* <div className={styles['feedback']}>
            <ButtonCore type="primary" onClick={() => navigate('/story')}>
              Create a new story
            </ButtonCore>
            <Feedback />
          </div> */}

          {/* Visibility Modal */}
          <VisibilityModal
            visible={isVisibilityModalVisible}
            isPrivate={data?.[0]?.IsPrivate || false}
            onConfirm={handleConfirmVisibility}
            onCancel={handleCancelVisibility}
            loading={isUpdatingVisibility}
          />

          {/* Rate Modal */}
          <RateModal visible={isRateModalVisible} onCancel={handleCancelRate} />

          {/* Learning Insight Modal */}
          <LearningInsightModal
            visible={isLearningInsightModalVisible}
            onCancel={handleCancelLearningInsight}
            analysis={analysisData || []}
            isLoading={isAnalysisLoading}
            error={analysisError}
          />

          {/* Activity Modal */}
          <ActivityModal
            visible={isActivityModalVisible}
            onCancel={handleCancelActivity}
            activity={data?.[0]?.Activity || null}
            storyName={data?.[0]?.StoryName || 'Story'}
          />

          {/* Hashtag Modal */}
          <HashtagModal
            visible={isHashtagModalVisible}
            onCancel={handleCancelHashtag}
            storyId={data?.[0]?.StoryID || null}
            storyName={data?.[0]?.StoryName || 'Story'}
          />

          {/* Story Request Modal */}
          <StoryRequestModal
            visible={isStoryRequestModalVisible}
            onCancel={handleCancelStoryRequest}
            storyRequestInfo={storyRequestData || null}
            isLoading={isStoryRequestLoading}
            error={storyRequestError}
          />

          {/* Recommended Stories Modal */}
          <RecommendedStoriesModal
            visible={isRecommendedStoriesModalVisible}
            onCancel={handleCancelRecommendedStories}
            recommendedStories={recommendedStoriesData || null}
            isLoading={isRecommendedStoriesLoading}
            error={recommendedStoriesError}
            isMobile={width <= 1000}
          />

          {/* story insight modal */}
          <StoryInsight
            storyInsights={storyInsights}
            isLoading={isStoryInSightLoading}
            error={storyInSightError}
            visible={isStoryInSightVisible}
            onCancel={() => setIsStoryInsightVisible(false)}
            isMobile={width <= 1000}
          />
        </div>
      ) : (
        <NotFound
          text="Story Not Found"
          button={
            <ButtonCore type="primary" onClick={() => navigate('/story')}>
              Create Story
            </ButtonCore>
          }
        />
      )}
    </>
  );
};

export default ViewStory;
